import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Factory, Plane, Newspaper } from "lucide-react";
import api from "../lib/api";
import { Spinner, Pill } from "../components/common";
import WikiPanel from "../components/WikiPanel";
import NewsFeed from "../components/NewsFeed";
import AiAircraftProfile from "../components/AiAircraftProfile";

export default function AircraftDetail() {
  const { code } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setA(null); setErr(false);
    api.get(`/aircraft/${encodeURIComponent(code)}`).then((r) => setA(r.data)).catch(() => setErr(true));
  }, [code]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">Aircraft not found.</div>;
  if (!a) return <Spinner full />;

  return (
    <div data-testid="aircraft-detail" className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
      <Link to="/aircraft" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-4"><ArrowLeft className="w-4 h-4" /> Aircraft</Link>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {a.iata && <Pill tone="blue">IATA {a.iata}</Pill>}
        {a.icao && <Pill>ICAO {a.icao}</Pill>}
      </div>
      <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight">{a.name}</h1>
      <Link to={`/manufacturers/${encodeURIComponent(a.manufacturer)}`} data-testid="aircraft-mfr-link"
        className="inline-flex items-center gap-2 mt-3 border border-zinc-300 bg-white px-3 py-2 text-sm hover:border-primary transition-colors">
        <Factory className="w-4 h-4 text-primary" /> {a.manufacturer}
      </Link>

      <div className="grid lg:grid-cols-3 gap-10 mt-8">
        {/* Left: AI profile + references */}
        <div className="lg:col-span-2 space-y-8">
          <AiAircraftProfile code={a.iata || a.icao || code} name={a.name} />
          <WikiPanel title={a.name} />
        </div>

        {/* Right: news + operators */}
        <div className="space-y-8">
          <div>
            <h2 className="font-heading font-700 text-xl mb-4 flex items-center gap-2"><Newspaper className="w-5 h-5 text-primary" /> Latest news</h2>
            <NewsFeed path={`/news?category=general&q=${encodeURIComponent(a.name)}&limit=8&archive_as=aircraft`} compact />
          </div>
          <div>
            <h2 className="font-heading font-700 text-xl mb-4 flex items-center gap-2"><Plane className="w-5 h-5 text-primary -rotate-45" /> Operators</h2>
            {a.operators?.length ? (
              <div className="space-y-2">
                {a.operators.map((o) => (
                  <Link key={o.id} to={`/airlines/${o.id}`} className="flex items-center justify-between border border-zinc-300 bg-white px-4 py-2.5 hover:border-primary transition-colors">
                    <span className="text-sm font-medium truncate">{o.name}</span>
                    <span className="font-mono text-xs text-primary">{o.iata}</span>
                  </Link>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No operator data available.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
